import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { adminSectorStats } from "../lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  } catch { return iso; }
}

// Continuous heat scale: cool blue → warm mint → warm ember → hot red
function heatColor(t) {
  const clamp = Math.max(0, Math.min(1, t));
  if (clamp < 0.2) return { bg: "#EEF2F7", ink: "#7A7A8A" };
  if (clamp < 0.4) return { bg: "#DDE7F0", ink: "#1A1A24" };
  if (clamp < 0.6) return { bg: "#B8D6EF", ink: "#1A1A24" };
  if (clamp < 0.8) return { bg: "#F0D48A", ink: "#1A1A24" };
  return { bg: "#E28A71", ink: "#1A1A24" };
}

function HeatBar({ heat }) {
  const width = `${Math.max(4, heat * 100)}%`;
  const { bg } = heatColor(heat);
  return (
    <div className="h-2 w-full rounded-full bg-rw-bg3 overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="h-full rounded-full"
        style={{ background: bg }}
      />
    </div>
  );
}

export default function SectorsTab({ adminKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState("orders"); // orders | revenue | avg

  const load = async () => {
    setLoading(true);
    try { setData(await adminSectorStats(adminKey)); }
    catch { toast.error("Couldn't load sector stats"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sorted = useMemo(() => {
    if (!data?.sectors) return [];
    const cmp = {
      orders: (a, b) => b.orders - a.orders || b.revenue - a.revenue,
      revenue: (a, b) => b.revenue - a.revenue,
      avg: (a, b) => b.avg_order_value - a.avg_order_value,
    }[sort];
    return [...data.sectors].sort(cmp);
  }, [data, sort]);

  if (loading && !data) return <div className="mt-8 text-rw-ink3 text-sm">Loading sector heat map…</div>;
  if (!data) return null;

  const empty = data.totals.sectors_with_orders === 0;

  return (
    <div className="mt-8" data-testid="sectors-tab">
      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-4 border border-rw-ink/10 rounded-2xl overflow-hidden bg-rw-bg2" data-testid="sectors-summary">
        <div className="p-5 border-r border-b md:border-b-0 border-rw-ink/10">
          <div className="overline">Total orders</div>
          <div className="font-display text-2xl mt-1">{data.totals.orders.toLocaleString("en-IN")}</div>
        </div>
        <div className="p-5 border-b md:border-b-0 md:border-r border-rw-ink/10">
          <div className="overline">Total revenue</div>
          <div className="font-display text-2xl mt-1">₹{data.totals.revenue.toLocaleString("en-IN")}</div>
        </div>
        <div className="p-5 border-r border-rw-ink/10">
          <div className="overline">Active sectors</div>
          <div className="font-display text-2xl mt-1">{data.totals.sectors_with_orders} / {data.totals.sector_count}</div>
        </div>
        <div className="p-5">
          <div className="overline">Highest concentration</div>
          <div className="font-display text-lg mt-1 truncate">{data.hottest[0]?.sector || "—"}</div>
          <div className="text-xs text-rw-ink3 mt-1">{data.hottest[0]?.orders || 0} orders</div>
        </div>
      </div>

      {empty && (
        <div className="mt-10 text-center text-rw-ink3 py-16 border border-dashed border-rw-ink/15 rounded-2xl">
          <div className="font-display text-xl text-rw-ink mb-2">No sector data yet</div>
          <div className="text-sm">Once orders come in, the heat map lights up.</div>
        </div>
      )}

      {!empty && (
        <>
          {/* Hottest + Coldest */}
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6" data-testid="hottest-panel">
              <div className="overline text-red-700">🔥 Invest here — hottest 5</div>
              <ul className="mt-3 divide-y divide-rw-ink/10">
                {data.hottest.map((s) => {
                  const c = heatColor(s.heat);
                  return (
                    <li key={s.sector} className="py-3 flex items-baseline gap-3" data-testid={`hottest-${s.sector}`}>
                      <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: c.bg }} />
                      <span className="flex-1 text-sm font-medium truncate">{s.sector}</span>
                      <span className="text-xs text-rw-ink3 tabular-nums">₹{s.revenue.toLocaleString("en-IN")}</span>
                      <span className="text-xs font-mono font-semibold text-rw-ink tabular-nums">{s.orders}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="text-xs text-rw-ink3 mt-4">Double down on ads + rider capacity here. Every ₹1 spent converts fastest.</p>
            </div>

            <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6" data-testid="coldest-panel">
              <div className="overline text-blue-700">❄️ Underperforming — grow next</div>
              <ul className="mt-3 divide-y divide-rw-ink/10">
                {data.coldest.length === 0 && <li className="py-3 text-sm text-rw-ink3">No underperforming sectors yet</li>}
                {data.coldest.map((s) => (
                  <li key={s.sector} className="py-3 flex items-baseline gap-3" data-testid={`coldest-${s.sector}`}>
                    <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 bg-rw-blue" />
                    <span className="flex-1 text-sm font-medium truncate">{s.sector}</span>
                    <span className="text-xs text-rw-ink3 tabular-nums">avg ₹{s.avg_order_value}</span>
                    <span className="text-xs font-mono font-semibold text-rw-ink tabular-nums">{s.orders}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-rw-ink3 mt-4">Try a first-order discount code targeting these pincodes on Instagram / Google Ads.</p>
            </div>
          </div>

          {/* Sort controls */}
          <div className="mt-8 flex flex-wrap gap-2 border-b border-rw-ink/10 pb-3">
            <span className="text-xs text-rw-ink3 self-center mr-2">Sort by</span>
            {[
              ["orders", "Orders"],
              ["revenue", "Revenue"],
              ["avg", "Avg order"],
            ].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setSort(k)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full ${sort === k ? "bg-rw-ink text-white" : "text-rw-ink2 hover:bg-rw-bg3"}`}
                data-testid={`sectors-sort-${k}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Full heat table */}
          <div className="mt-4 rounded-2xl border border-rw-ink/10 overflow-hidden bg-rw-bg2">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sectors-table">
                <thead className="bg-rw-bg3 text-rw-ink3">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Sector</th>
                    <th className="text-left px-4 py-3 font-semibold w-1/3">Concentration</th>
                    <th className="text-right px-4 py-3 font-semibold">Orders</th>
                    <th className="text-right px-4 py-3 font-semibold">Revenue</th>
                    <th className="text-right px-4 py-3 font-semibold">Avg</th>
                    <th className="text-right px-4 py-3 font-semibold">Subs</th>
                    <th className="text-right px-4 py-3 font-semibold">Last order</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((s) => {
                    const c = heatColor(s.heat);
                    return (
                      <tr key={s.sector} className="border-t border-rw-ink/10" data-testid={`row-${s.sector}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="inline-block w-2 h-2 rounded-full" style={{ background: c.bg }} />
                            <span className="font-medium">{s.sector}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3"><HeatBar heat={s.heat} /></td>
                        <td className="px-4 py-3 text-right tabular-nums">{s.orders}</td>
                        <td className="px-4 py-3 text-right tabular-nums">₹{s.revenue.toLocaleString("en-IN")}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-rw-ink2">₹{s.avg_order_value}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-rw-ink2">{s.active_subscriptions || "·"}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-rw-ink3 text-xs">{fmtDate(s.last_order_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
